import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, X } from 'lucide-react';
import { MAX_TICKET_PHOTOS } from '@nbh/shared';
import { compressImage } from '../../lib/image';
import { randomUuid } from '../../lib/uuid';

export interface PendingPhoto {
  id: string;
  previewUrl: string;
  blob: Blob;
}

export interface PhotoPickerProps {
  photos: PendingPhoto[];
  onAdd: (photo: PendingPhoto) => void;
  onRemove: (id: string) => void;
  onError: (message: string) => void;
}

/**
 * Camera capture with client-side compression.
 *
 * `capture="environment"` opens the rear camera directly rather than the gallery,
 * which is what a technician standing in front of a fault actually wants.
 *
 * Stops at the number the contract accepts (`MAX_TICKET_PHOTOS`), and says so. Unbounded,
 * an eleventh photo uploaded fine and then failed the report's own Zod validation — and a
 * server rejection is not the offline case, so the report was not queued either and the
 * whole thing was lost. The cheapest place to enforce a shared limit is the side holding
 * the camera.
 */
export function PhotoPicker({ photos, onAdd, onRemove, onError }: PhotoPickerProps) {
  const { t } = useTranslation();
  const fileInput = useRef<HTMLInputElement>(null);

  const full = photos.length >= MAX_TICKET_PHOTOS;

  async function handleFile(files: FileList | null) {
    const file = files?.[0];
    if (!file) return;

    // Refused out loud, never dropped quietly: a photo that vanishes without a word
    // teaches people the camera is broken.
    if (full) {
      onError(t('report.photoLimit', { max: MAX_TICKET_PHOTOS }));
      if (fileInput.current) fileInput.current.value = '';
      return;
    }

    try {
      // Compress before anything else: a 5MB original must never reach the queue.
      const compressed = await compressImage(file);
      onAdd({
        id: randomUuid(),
        previewUrl: URL.createObjectURL(compressed.blob),
        blob: compressed.blob,
      });
    } catch {
      onError(t('report.photoFailed'));
    } finally {
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  return (
    <div>
      <button
        type="button"
        disabled={full}
        onClick={() => fileInput.current?.click()}
        className="flex min-h-[56px] w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-600 bg-transparent text-base font-medium text-slate-200 disabled:border-slate-800 disabled:text-slate-500"
      >
        <Camera className="size-5" aria-hidden="true" />
        {t('report.addPhoto')}
      </button>

      {/* Says why the button is dead rather than leaving the reporter to guess, and says
          it before they have taken a shot that cannot be kept. */}
      {full && (
        <p className="mt-2 text-xs text-amber-400">
          {t('report.photoLimit', { max: MAX_TICKET_PHOTOS })}
        </p>
      )}
      <input
        ref={fileInput}
        type="file"
        accept="image/*"
        capture="environment"
        className="sr-only"
        aria-label={t('report.addPhoto')}
        onChange={(event) => void handleFile(event.target.files)}
      />

      {photos.length > 0 && (
        <ul className="mt-3 flex flex-wrap gap-2">
          {photos.map((photo) => (
            <li key={photo.id} className="relative">
              <img src={photo.previewUrl} alt="" className="size-20 rounded-lg object-cover" />
              <button
                type="button"
                onClick={() => onRemove(photo.id)}
                aria-label={t('report.removePhoto')}
                className="absolute -right-2 -top-2 flex size-7 min-h-0 items-center justify-center rounded-full bg-slate-800 p-0 text-slate-200"
              >
                <X className="size-4" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
